import React from 'react';
import { tierName, tierColorVar } from '../constants/achievements';
import '../styles/BadgeDetailModal.css';

const formatEarnedAt = (iso) => {
    if (!iso) return null;
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

/**
 * Detail sheet for a badge from the collection grid.
 *
 * For a tiered family the body is a tier ladder (every threshold with its own
 * earned state and reward); for a standalone achievement it is a single row.
 */
const BadgeDetailModal = ({ badge, onClose }) => {
    if (!badge) return null;

    const { name, description, icon, earned, complete, tierIndex, progress, progressTarget } = badge;
    const showProgress = !complete && progress != null && progressTarget != null;
    const pct = showProgress
        ? Math.min(100, Math.round((progress / progressTarget) * 100))
        : 0;

    return (
        <div className="sc-overlay" onClick={onClose}>
            <div
                className="sc-modal ach-modal"
                role="dialog"
                aria-modal="true"
                aria-label={name}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ach-modal-head">
                    <span
                        className={`ach-modal-icon ${earned ? 'is-earned' : 'is-locked'}`}
                        style={earned
                            ? { borderColor: badge.family ? tierColorVar(tierIndex) : 'var(--color-primary)' }
                            : undefined}
                        aria-hidden="true"
                    >
                        {icon || '🏆'}
                    </span>
                    <div className="ach-modal-heading">
                        <h3 className="ach-modal-title">{name}</h3>
                        {badge.family && (
                            <p className="ach-modal-sub">
                                {badge.earnedCount}/{badge.totalCount} tiers
                                {earned && ` · ${tierName(tierIndex)}`}
                            </p>
                        )}
                        {!badge.family && badge.recurrence !== 'once' && (
                            <p className="ach-modal-sub">
                                {badge.recurrence === 'daily' ? 'Repeats daily' : 'Repeats weekly'}
                            </p>
                        )}
                    </div>
                </div>

                {description && <p className="ach-modal-desc">{description}</p>}

                {showProgress && (
                    <div className="ach-progress ach-modal-progress">
                        <div className="ach-progress-track">
                            <div className="ach-progress-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="ach-progress-label">
                            {progress}/{progressTarget}
                        </span>
                    </div>
                )}

                <ul className="ach-modal-tiers">
                    {badge.tiers.map((tier, index) => {
                        const earnedOn = formatEarnedAt(tier.earned_at);
                        return (
                            <li
                                key={tier.id}
                                className={`ach-modal-tier ${tier.earned ? 'is-earned' : 'is-locked'}`}
                            >
                                <span
                                    className="ach-modal-tier-dot"
                                    style={tier.earned
                                        ? { background: badge.family ? tierColorVar(index) : 'var(--color-primary)' }
                                        : undefined}
                                    aria-hidden="true"
                                />
                                <span className="ach-modal-tier-label">
                                    {badge.family ? tierName(index) : tier.name}
                                    {badge.family && tier.progress_target != null && (
                                        <span className="ach-modal-tier-goal"> · {tier.progress_target}</span>
                                    )}
                                </span>
                                <span className="ach-modal-tier-meta">
                                    {tier.earned && earnedOn ? earnedOn : `🪙 ${tier.spudcoin_reward}`}
                                </span>
                            </li>
                        );
                    })}
                </ul>

                <div className="ach-modal-actions">
                    <button type="button" className="sc-btn sc-btn--secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BadgeDetailModal;
